"use client";

import * as React from "react";
import {
  Calendar,
  UserPlus,
  CheckCircle,
  DollarSign,
  Clock,
  CalendarPlus,
  CalendarCheck,
  CalendarX,
  UserRoundPlus,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Badge, Avatar, Skeleton } from "@/components/ui";
import { AppointmentsAreaChart, ProceduresDonutChart, Sparkline } from "@/components/dashboard";
import { formatCurrency } from "@/lib/utils";
import {
  appointmentsService,
  patientsService,
  transactionsService,
  type Appointment,
  type Patient,
  type FinancialSummary,
} from "@/lib/api";

const statusConfig = {
  SCHEDULED: { label: "Agendado", variant: "info" as const },
  CONFIRMED: { label: "Confirmado", variant: "success" as const },
  COMPLETED: { label: "Realizado", variant: "secondary" as const },
  CANCELLED: { label: "Cancelado", variant: "error" as const },
  NO_SHOW: { label: "Não compareceu", variant: "warning" as const },
  IN_PROGRESS: { label: "Em andamento", variant: "info" as const },
};

const activityIcons = {
  appointment_created: CalendarPlus,
  appointment_confirmed: CalendarCheck,
  appointment_cancelled: CalendarX,
  patient_created: UserRoundPlus,
};

const activityColors = {
  appointment_created: "bg-accent/10 text-accent",
  appointment_confirmed: "bg-status-success/10 text-status-success",
  appointment_cancelled: "bg-status-error/10 text-status-error",
  patient_created: "bg-status-info/10 text-status-info",
};

function formatActivityTime(timestamp: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);

  if (minutes < 60) return `Há ${minutes} minutos`;
  if (hours < 24) return `Há ${hours} hora${hours > 1 ? "s" : ""}`;
  return `Há ${Math.floor(hours / 24)} dia${hours >= 48 ? "s" : ""}`;
}

export function HealthcareDashboard() {
  const [appointments, setAppointments] = React.useState<Appointment[]>([]);
  const [patients, setPatients] = React.useState<Patient[]>([]);
  const [summary, setSummary] = React.useState<FinancialSummary | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    async function fetchData() {
      try {
        const [appointmentsRes, patientsRes, summaryRes] = await Promise.all([
          appointmentsService.list({ limit: 100 }),
          patientsService.list({ limit: 100 }),
          transactionsService.getSummary(),
        ]);
        setAppointments(appointmentsRes.data);
        setPatients(patientsRes.data);
        setSummary(summaryRes);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split("T")[0];

  const appointmentsToday = appointments.filter((apt) => apt.date === todayStr).length;

  const thisMonth = new Date();
  thisMonth.setDate(1);
  thisMonth.setHours(0, 0, 0, 0);
  const newPatientsMonth = patients.filter((p) => new Date(p.createdAt) >= thisMonth).length;

  const completedAppointments = appointments.filter((apt) => apt.status === "COMPLETED").length;
  const totalAppointments = appointments.filter((apt) => apt.status !== "CANCELLED").length;
  const attendanceRate = totalAppointments > 0 ? Math.round((completedAppointments / totalAppointments) * 100) : 0;

  const monthlyRevenue = summary?.totalRevenue || 0;

  const upcomingAppointments = appointments
    .filter((apt) => apt.date >= todayStr && apt.status !== "CANCELLED")
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, 5);

  const weeklyAppointmentsData = [
    { name: "Seg", agendamentos: Math.round(appointments.length * 0.15), faturamento: 0 },
    { name: "Ter", agendamentos: Math.round(appointments.length * 0.18), faturamento: 0 },
    { name: "Qua", agendamentos: Math.round(appointments.length * 0.22), faturamento: 0 },
    { name: "Qui", agendamentos: Math.round(appointments.length * 0.20), faturamento: 0 },
    { name: "Sex", agendamentos: Math.round(appointments.length * 0.25), faturamento: 0 },
  ];

  const procedureDistributionData = summary?.byProcedure.slice(0, 5).map((item, idx) => ({
    name: item.procedureName,
    value: item.count,
    color: ["#C9A87C", "#8BA888", "#A29BFE", "#F8B739", "#FF6B6B"][idx % 5],
  })) || [];

  const attendanceSparklineData = [
    { value: 85 },
    { value: 88 },
    { value: 82 },
    { value: 90 },
    { value: 87 },
    { value: attendanceRate },
  ];

  const activities = appointments.slice(0, 5).map((apt) => ({
    id: apt.id,
    type: apt.status === "CANCELLED" ? "appointment_cancelled" : "appointment_created",
    description: `${apt.patient?.name || "Paciente"} - ${apt.procedure?.name || "Procedimento"}`,
    timestamp: apt.createdAt,
  }));

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-80 lg:col-span-2" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Metrics Cards */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/10">
                <Calendar className="h-6 w-6 text-accent" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-foreground-secondary">Agendamentos Hoje</p>
                <p className="font-outfit text-3xl font-light text-foreground">
                  {appointmentsToday}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sage/10">
                <UserPlus className="h-6 w-6 text-sage" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-foreground-secondary">Pacientes Novos</p>
                <p className="font-outfit text-3xl font-light text-foreground">
                  {newPatientsMonth}
                </p>
                <p className="text-xs text-foreground-secondary">este mês</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-status-info/10">
                  <CheckCircle className="h-6 w-6 text-status-info" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm text-foreground-secondary">Taxa de Comparecimento</p>
                  <p className="font-outfit text-3xl font-light text-foreground">
                    {attendanceRate}%
                  </p>
                </div>
              </div>
              <div className="w-20">
                <Sparkline data={attendanceSparklineData} color="#A29BFE" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/10">
                <DollarSign className="h-6 w-6 text-accent" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-foreground-secondary">Faturamento Total</p>
                <p className="font-outfit text-3xl font-light text-foreground">
                  {formatCurrency(monthlyRevenue)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart + Upcoming Appointments */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Agendamentos por Semana</CardTitle>
          </CardHeader>
          <CardContent>
            <AppointmentsAreaChart data={weeklyAppointmentsData} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Próximos Agendamentos</CardTitle>
            <a
              href="/agenda"
              className="text-sm text-accent transition-colors hover:text-accent-hover"
            >
              Ver todos →
            </a>
          </CardHeader>
          <CardContent className="space-y-3">
            {upcomingAppointments.length > 0 ? (
              upcomingAppointments.map((appointment) => (
                <div
                  key={appointment.id}
                  className="flex items-center gap-3 rounded-xl p-3 transition-colors hover:bg-gray-50"
                >
                  <Avatar name={appointment.patient?.name || ""} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {appointment.patient?.name || "Paciente"}
                    </p>
                    <p className="truncate text-xs text-foreground-secondary">
                      {appointment.procedure?.name || "Procedimento"}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-sm text-foreground">
                      <Clock className="h-3 w-3" strokeWidth={1.5} />
                      {appointment.startTime}
                    </div>
                    <Badge
                      variant={
                        statusConfig[appointment.status as keyof typeof statusConfig]?.variant || "outline"
                      }
                    >
                      {statusConfig[appointment.status as keyof typeof statusConfig]?.label || appointment.status}
                    </Badge>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-sm text-foreground-secondary py-8">
                Nenhum agendamento próximo
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Procedures chart + Activity */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Procedimentos Mais Realizados</CardTitle>
          </CardHeader>
          <CardContent>
            {procedureDistributionData.length > 0 ? (
              <ProceduresDonutChart data={procedureDistributionData} />
            ) : (
              <p className="text-center text-sm text-foreground-secondary py-16">
                Nenhum procedimento realizado
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Atividade Recente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {activities.length > 0 ? (
                activities.map((activity, index) => {
                  const Icon = activityIcons[activity.type as keyof typeof activityIcons] || CalendarPlus;
                  const colorClass = activityColors[activity.type as keyof typeof activityColors] || "bg-accent/10 text-accent";

                  return (
                    <div key={activity.id} className="flex gap-4">
                      <div className="relative flex flex-col items-center">
                        <div
                          className={`flex h-9 w-9 items-center justify-center rounded-full ${colorClass}`}
                        >
                          <Icon className="h-4 w-4" strokeWidth={1.5} />
                        </div>
                        {index < activities.length - 1 && (
                          <div className="mt-2 h-full w-px bg-gray-100" />
                        )}
                      </div>
                      <div className="flex-1 pb-4">
                        <p className="text-sm text-foreground">{activity.description}</p>
                        <p className="text-xs text-foreground-secondary">
                          {formatActivityTime(activity.timestamp)}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-center text-sm text-foreground-secondary py-8">
                  Nenhuma atividade recente
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
